import React, { useCallback, useState } from 'react';
import { FontStyles } from './FontStyles';
import { ColorStyle } from './ColorStyle';
import { TextFieldWithAdornment } from './TextFieldWithAdornment';
import { DropdownStyle } from './DropdownStyle';
import { ToggleActiveButton } from './ToggleActiveButton';
import { getErrorMessage } from '../../../../../get-error-message';
import { borderStyle } from '../../../../../constants/border-style';
import { WidgetMaker } from '../../../../../types/widget-maker';
import { CampaignDraft } from '../../../../../types/campaign-draft';
import { ParseJoiValidateResponse } from '../../../../../../../utils';
import {
  FontStyle,
  FontStyleRow,
  SectionStyled,
  TextBoxStyled,
} from './EmailDisplaySettingsSection.styles';

interface ButtonSectionProps {
  widgetMaker: Partial<WidgetMaker>;
  onChange: (campaign: CampaignDraft) => void;
  errors: ParseJoiValidateResponse<WidgetMaker>;
}

export const ButtonSection = ({ widgetMaker, onChange, errors }: ButtonSectionProps) => {
  const buttonConfig = widgetMaker?.CTAButton;

  const [isActive, setIsActive] = useState<boolean>(buttonConfig?.active);

  const handleCTAButtonChange = useCallback(
    (field, value) => {
      onChange({
        widgetMaker: {
          ...widgetMaker,
          CTAButton: {
            ...buttonConfig,
            [field]: value,
          },
        },
      });
    },
    [onChange, widgetMaker]
  );

  const onToggleClick = useCallback(() => {
    setIsActive(!isActive);
    handleCTAButtonChange('active', !isActive);
  }, [setIsActive, handleCTAButtonChange]);

  return (
    <SectionStyled>
      {isActive ? (
        <>
          <TextBoxStyled
            label='Call to action text'
            placeholder='type...'
            value={buttonConfig?.text || ''}
            onChange={value => handleCTAButtonChange('text', value)}
          />
          <FontStyles
            config={buttonConfig}
            onChange={handleCTAButtonChange}
            getErrorMessage={fieldKey => getErrorMessage(errors, 'CTAButton', fieldKey)}
          />
          <FontStyleRow>
            <ColorStyle
              field='backgroundColor'
              label='Background color'
              defaultColor='#000000'
              color={buttonConfig?.backgroundColor}
              onChange={handleCTAButtonChange}
            />
            <FontStyle width={42}>
              <TextFieldWithAdornment
                field='borderRadius'
                label='Border radius'
                defaultValue={0}
                value={buttonConfig?.borderRadius}
                adornment='px'
                onChange={handleCTAButtonChange}
              />
            </FontStyle>
          </FontStyleRow>
          <FontStyleRow>
            <FontStyle width={50}>
              <TextFieldWithAdornment
                field='borderWidth'
                label='Border width'
                defaultValue={0}
                value={buttonConfig?.borderWidth}
                adornment='px'
                onChange={handleCTAButtonChange}
              />
            </FontStyle>
            <FontStyle width={50}>
              <DropdownStyle
                data={borderStyle}
                field='borderStyle'
                defaultValue='none'
                value={buttonConfig?.borderStyle}
                onChange={handleCTAButtonChange}
                label='Border style'
              />
            </FontStyle>
          </FontStyleRow>
          <FontStyleRow>
            <FontStyle width={60}>
              <ColorStyle
                field='borderColor'
                label='Border color'
                defaultColor='#000000'
                color={buttonConfig?.borderColor}
                onChange={handleCTAButtonChange}
              />
            </FontStyle>
          </FontStyleRow>
          <ToggleActiveButton onToggleClick={onToggleClick} isActive={isActive} title='button' />
        </>
      ) : (
        <ToggleActiveButton onToggleClick={onToggleClick} isActive={isActive} title='button' />
      )}
    </SectionStyled>
  );
};
